import React, { useEffect, useState } from 'react';
import './UserManagement.css';
import { logCreation, logUpdate, logDeletion } from './auditLog';

const UserManagement = ({ currentUser }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [showAddUserForm, setShowAddUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPasswords, setShowPasswords] = useState(true);
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    name: '',
    role: 'user',
  });

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 5000);
  };

  const parseApiResponse = async (response) => {
    const rawText = await response.text();
    let data = null;

    if (rawText) {
      try {
        data = JSON.parse(rawText);
      } catch (_error) {
        if (!response.ok) {
          throw new Error('Server returned an invalid response. Please check backend API status.');
        }
      }
    }

    if (!response.ok || (data && data.success === false)) {
      throw new Error(data?.message || `Request failed with status ${response.status}`);
    }

    return data || { success: true };
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      const siteId = currentUser?.site_id || 'hosp_001';
      const response = await fetch(`/api/users?site_id=${encodeURIComponent(siteId)}`);
      const data = await parseApiResponse(response);

      setUsers(data.data || []);
    } catch (error) {
      showMessage(error.message || 'Failed to load users', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewUser((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditingUser((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const addUser = async (e) => {
    e.preventDefault();

    if (!newUser.username || !newUser.password || !newUser.name) {
      showMessage('Please fill in all required fields', 'error');
      return;
    }

    try {
      const response = await fetch('/api/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...newUser, site_id: currentUser?.site_id || 'hosp_001' }),
      });
      await parseApiResponse(response);

      showMessage('User added successfully!', 'success');
      logCreation('USER', newUser.username, `User '${newUser.username}' (${newUser.role}) created`, currentUser);
      setNewUser({ username: '', password: '', name: '', role: 'user' });
      setShowAddUserForm(false);
      loadUsers();
    } catch (error) {
      showMessage(error.message || 'Failed to add user', 'error');
    }
  };

  const startEditing = (user) => {
    setEditingUser({ ...user, password: user.password || '', _originalId: user.id });
  };

  const saveEditing = async () => {
    if (!editingUser.username || !editingUser.name) {
      showMessage('Please fill in all required fields', 'error');
      return;
    }

    try {
      const { _originalId, ...userPayload } = editingUser;
      const response = await fetch(`/api/users/${_originalId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...userPayload, site_id: currentUser?.site_id || 'hosp_001' }),
      });
      await parseApiResponse(response);

      showMessage('User updated successfully!', 'success');
      logUpdate('USER', editingUser.username, `User '${editingUser.username}' (${editingUser.role}) updated`, currentUser);
      setEditingUser(null);
      loadUsers();
    } catch (error) {
      showMessage(error.message || 'Failed to update user', 'error');
    }
  };

  const deleteUser = async (id) => {
    if (id === currentUser.id) {
      showMessage('You cannot delete your own account', 'error');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      const siteId = currentUser?.site_id || 'hosp_001';
      const response = await fetch(`/api/users/${id}?site_id=${encodeURIComponent(siteId)}`, { method: 'DELETE' });
      await parseApiResponse(response);

      showMessage('User deleted successfully!', 'success');
      logDeletion('USER', String(id), `User ID '${id}' deleted`, currentUser);
      loadUsers();
    } catch (error) {
      showMessage(error.message || 'Failed to delete user', 'error');
    }
  };

  if (currentUser.role !== 'manager' && currentUser.role !== 'admin') {
    return (
      <div className="user-management-container">
        <div className="card">
          <h2 className="card-title">User Management</h2>
          <div className="message error">Access denied. Only managers/admins can manage users.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="user-management-container">
      <div className="card">
        <h2 className="card-title">User Management</h2>

        {/* Hospital info */}
        <div style={{ background: '#eaf3ff', border: '1px solid #b3d0f5', borderRadius: '6px', padding: '12px 16px', marginBottom: '16px' }}>
          <strong>Hospital:</strong> {currentUser?.site_name || 'Zydus Hospital (Default)'}
          <span style={{ marginLeft: '16px', color: '#555', fontSize: '0.85rem' }}>
            Site ID: {currentUser?.site_id || 'hosp_001'}
          </span>
        </div>

        {/* Add Hospital notice */}
        <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: '6px', padding: '10px 16px', marginBottom: '16px', fontSize: '0.9rem' }}>
          <strong>Add Hospital (Site):</strong> To register a new hospital site, log in as <em>Super Admin</em> using the "System Administration" link on the login page.
        </div>

        {currentUser && (
          <div className="user-badge">
            Logged in as: {currentUser.name} ({currentUser.role})
          </div>
        )}

        {message.text && (
          <div className={`message ${message.type}`}>
            {message.type === 'success' ? 'Success:' : 'Error:'} {message.text}
          </div>
        )}

        {loading && <div className="message">Loading users...</div>}

        <div className="user-controls">
          <button className="btn btn-primary" onClick={() => setShowAddUserForm(!showAddUserForm)}>
            {showAddUserForm ? 'Cancel' : '+ Add New User'}
          </button>
          <button className="btn btn-secondary" onClick={() => setShowPasswords((prev) => !prev)}>
            {showPasswords ? 'Hide Passwords' : 'Show Passwords'}
          </button>
        </div>

        {showAddUserForm && (
          <div className="add-user-form">
            <h3>Add New User</h3>
            <form onSubmit={addUser}>
              <div className="form-group">
                <label htmlFor="username">Username *</label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={newUser.username}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password *</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={newUser.password}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="name">Full Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={newUser.name}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="role">Role</label>
                <select id="role" name="role" value={newUser.role} onChange={handleInputChange}>
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                  <option value="manager">Manager</option>
                </select>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-success">Add User</button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAddUserForm(false);
                    setNewUser({ username: '', password: '', name: '', role: 'user' });
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="current-user-info">
          <h3>Current User Information</h3>
          <div className="user-info-card">
            <p><strong>Name:</strong> {currentUser.name}</p>
            <p><strong>Username:</strong> {currentUser.username}</p>
            <p><strong>Role:</strong> {currentUser.role}</p>
          </div>
        </div>

        <div className="users-list">
          <h3>All Users</h3>
          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Username</th>
                  <th>Password</th>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) =>
                  editingUser && editingUser._originalId === user.id ? (
                    <tr key={user.id}>
                      <td>
                        <input
                          type="text"
                          name="id"
                          value={editingUser.id}
                          onChange={handleEditChange}
                          style={{ width: '70px' }}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          name="username"
                          value={editingUser.username}
                          onChange={handleEditChange}
                        />
                      </td>
                      <td>
                        <input
                          type={showPasswords ? 'text' : 'password'}
                          name="password"
                          value={editingUser.password || ''}
                          onChange={handleEditChange}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          name="name"
                          value={editingUser.name}
                          onChange={handleEditChange}
                        />
                      </td>
                      <td>
                        <select name="role" value={editingUser.role} onChange={handleEditChange}>
                          <option value="user">User</option>
                          <option value="admin">Admin</option>
                          <option value="manager">Manager</option>
                        </select>
                      </td>
                      <td>
                        <button className="btn btn-success" onClick={saveEditing}>Save</button>
                        <button className="btn btn-secondary" onClick={() => setEditingUser(null)}>Cancel</button>
                      </td>
                    </tr>
                  ) : (
                    <tr key={user.id}>
                      <td>{user.id}</td>
                      <td>{user.username}</td>
                      <td>{showPasswords ? (user.password || '') : '••••••••'}</td>
                      <td>{user.name}</td>
                      <td>
                        <span className={`role-badge role-${user.role}`}>{user.role}</span>
                      </td>
                      <td>
                        <button className="btn btn-primary" onClick={() => startEditing(user)}>Edit</button>
                        <button
                          className="btn btn-danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            deleteUser(user.id);
                          }}
                          disabled={user.id === currentUser.id}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;

